import { X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cvBankUploadSchema, type CvBankDuplicatePair, type CvBankUploadResult } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { Drawer } from '../../../components/Drawer';
import { DropZone } from '../../../components/DropZone';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { cvBankApi, useRefreshCvBank } from '../api/cv-bank-api';
import { useResolveDuplicate } from '../hooks/use-cv-bank-actions';
import { cleanPhone } from '../cv-bank-format';
import styles from '../CvBank.module.css';
import { DuplicateChooser } from './DuplicateChooser';

const CV_ACCEPT = '.pdf,.doc,.docx';

type RowState = 'reading' | 'ready' | 'adding' | 'added' | 'failed';

interface UploadRow {
  readonly key: string;
  readonly file: File;
  readonly state: RowState;
  readonly fullName: string;
  readonly email: string;
  readonly phone: string;
  /** Per field, from the shared schema or the server (§L26 inline messages). */
  readonly errors: Readonly<Record<string, string>>;
  readonly note: string | null;
}

const OUTCOME_NOTES: Readonly<Record<CvBankUploadResult['outcome'], string>> = {
  created: 'Added',
  linked: 'Added to the record we already had',
  flagged: 'Added; may be someone already in the system',
};

let nextKey = 0;

/**
 * ADR 0046 §2 — CVs dropped straight into the bank. Each file's name, email and phone are read
 * off it and filled in for a person to check; "Add to CV Bank" then puts each through the same
 * duplicate check as any hand entry, with no job. A duplicate it finds is asked about at once.
 */
export function UploadDrawer({ isOpen, onClose }: { readonly isOpen: boolean; readonly onClose: () => void }): ReactNode {
  const show = useToasts((state) => state.show);
  const refresh = useRefreshCvBank();
  const resolve = useResolveDuplicate();
  const [rows, setRows] = useState<readonly UploadRow[]>([]);
  const [hasConsent, setHasConsent] = useState(false);
  const [consentProblem, setConsentProblem] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<readonly CvBankDuplicatePair[]>([]);

  const patch = (key: string, change: Partial<UploadRow>): void => setRows((current) => current.map((row) => (row.key === key ? { ...row, ...change } : row)));

  const takeFile = (file: File): void => {
    const key = `upload-${(nextKey += 1)}`;
    setRows((current) => [...current, { key, file, state: 'reading', fullName: '', email: '', phone: '', errors: {}, note: null }]);
    void cvBankApi
      .suggest(file)
      .then((suggestion) => patch(key, { state: 'ready', fullName: suggestion.fullName, email: suggestion.email ?? '', phone: suggestion.phone ?? '' }))
      .catch((error: unknown) => patch(key, { state: 'failed', note: errorMessage(error) }));
  };

  const waiting = rows.filter((row) => row.state === 'ready');

  const addAll = async (): Promise<void> => {
    if (!hasConsent) {
      setConsentProblem('Confirm they agreed to us keeping their CVs.');
      return;
    }
    const found: CvBankDuplicatePair[] = [];
    let added = 0;
    for (const row of waiting) {
      const fields = { fullName: row.fullName, email: blankToNull(row.email), phone: cleanPhone(row.phone), hasConsent: true as const };
      const checked = cvBankUploadSchema.safeParse(fields);
      if (!checked.success) {
        patch(row.key, { errors: fieldErrors(checked.error.issues) });
        continue;
      }
      patch(row.key, { state: 'adding', errors: {} });
      try {
        const result = await cvBankApi.upload(row.file, fields);
        added += result.addedEntryIds.length;
        found.push(...result.duplicates);
        patch(row.key, { state: 'added', note: OUTCOME_NOTES[result.outcome] });
      } catch (error) {
        patch(row.key, { state: 'ready', errors: error instanceof ApiError ? error.fields : {}, note: errorMessage(error) });
      }
    }
    // The toast says it at once; the bank refreshes behind it (§5a).
    if (added > 0) show({ tone: 'success', message: `${added} ${added === 1 ? 'CV' : 'CVs'} added to the CV Bank` });
    setDuplicates(found);
    void refresh();
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Upload CVs" meta="PDF, DOC or DOCX — one person per file" size="wide">
      <div className={styles.upload}>
        <DropZone label="Choose CVs to upload" title="Drop CVs here" hint="Name, email and phone are filled in from each CV for you to check." accept={CV_ACCEPT} isPending={false} isMultiple onFile={takeFile} />

        {duplicates.length > 0 && (
          <section className={styles.upload} aria-label="Possible duplicates">
            <h3 className={styles.sectionTitle}>Possibly already in the CV Bank</h3>
            {duplicates.map((pair) => (
              <DuplicateChooser
                key={`${pair.first.id}-${pair.second.id}`}
                pair={pair}
                canEdit
                onChoose={(keep) => {
                  setDuplicates((current) => current.filter((other) => other !== pair));
                  void resolve(pair, keep);
                }}
              />
            ))}
          </section>
        )}

        {rows.length > 0 && (
          <ul className={styles.uploadRows}>
            {rows.map((row) => (
              <li key={row.key} className={styles.uploadRow} data-state={row.state}>
                <div className={styles.uploadHead}>
                  <span className={styles.fileName}>{row.file.name}</span>
                  <RowStatus row={row} />
                  {(row.state === 'ready' || row.state === 'failed' || row.state === 'added') && (
                    <Button variant="ghost" size="sm" icon={X} aria-label={`Take ${row.file.name} off the list`} onClick={() => setRows((current) => current.filter((other) => other.key !== row.key))} />
                  )}
                </div>
                {(row.state === 'ready' || row.state === 'adding') && (
                  <div className={styles.uploadFields}>
                    <TextField label="Full name" value={row.fullName} error={row.errors.fullName} onChange={(fullName) => patch(row.key, { fullName })} />
                    <TextField label="Email" value={row.email} error={row.errors.email} onChange={(email) => patch(row.key, { email })} />
                    <TextField label="Mobile" value={row.phone} error={row.errors.phone} onChange={(phone) => patch(row.key, { phone })} />
                  </div>
                )}
                {row.note && row.state !== 'added' && <p className={styles.problem}>{row.note}</p>}
              </li>
            ))}
          </ul>
        )}

        {waiting.length > 0 && (
          <div className={styles.uploadFooter}>
            <Checkbox
              checked={hasConsent}
              onChange={(checked) => {
                setHasConsent(checked);
                setConsentProblem(null);
              }}
              label="They agreed to us keeping their CVs"
            />
            {consentProblem && <p className={styles.problem}>{consentProblem}</p>}
            <div className={styles.panelActions}>
              <Button variant="solid" onClick={() => void addAll()}>
                {waiting.length === 1 ? 'Add to CV Bank' : `Add ${waiting.length} to CV Bank`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}

function RowStatus({ row }: { readonly row: UploadRow }): ReactNode {
  switch (row.state) {
    case 'reading':
      return <span className={styles.meta}>Reading…</span>;
    case 'adding':
      return <span className={styles.meta}>Adding…</span>;
    case 'added':
      return <Chip tone="success">{row.note ?? 'Added'}</Chip>;
    case 'failed':
      return <Chip tone="danger">Not added</Chip>;
    case 'ready':
      return null;
  }
}

interface TextFieldProps {
  readonly label: string;
  readonly value: string;
  readonly error: string | undefined;
  readonly onChange: (value: string) => void;
}

function TextField({ label, value, error, onChange }: TextFieldProps): ReactNode {
  return (
    <Field label={label} {...(error ? { error } : {})}>
      {(ids) => <Input id={ids.inputId} aria-describedby={ids.describedBy} isInvalid={ids.isInvalid} value={value} onChange={(event) => onChange(event.target.value)} />}
    </Field>
  );
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function fieldErrors(issues: readonly { readonly path: readonly PropertyKey[]; readonly message: string }[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) {
    const key = issue.path.map(String).join('.') || '_';
    out[key] ??= issue.message;
  }
  return out;
}
